import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, CheckCircle2, Copy, Ticket, CreditCard } from 'lucide-react'
import { cn } from '@/lib/utils'
import confetti from 'canvas-confetti'

interface RedeemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  balance: number
  subscriptionValue: number
  isSubscriber: boolean
  onSuccess: () => void
}

export function RedeemDialog({
  open,
  onOpenChange,
  balance,
  subscriptionValue,
  isSubscriber,
  onSuccess,
}: RedeemDialogProps) {
  const [step, setStep] = useState<'select' | 'confirm' | 'processing' | 'success'>('select')
  const [selectedOption, setSelectedOption] = useState<'subscription' | 'coupon'>(
    isSubscriber ? 'subscription' : 'coupon'
  )
  const [amount, setAmount] = useState(balance)

  // Reset state when opening
  if (!open && step !== 'select') {
    setTimeout(() => setStep('select'), 300)
  }

  const handleConfirm = async () => {
    setStep('processing')

    // Simulate API call
    setTimeout(() => {
      setStep('success')
      fireConfetti()
      onSuccess()
    }, 2000)
  }

  const fireConfetti = () => {
    const duration = 3000
    const animationEnd = Date.now() + duration
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 }

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min

    const interval = setInterval(function () {
      const timeLeft = animationEnd - Date.now()

      if (timeLeft <= 0) {
        return clearInterval(interval)
      }

      const particleCount = 50 * (timeLeft / duration)

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      })
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      })
    }, 250)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        {step === 'select' && (
          <>
            <DialogHeader>
              <DialogTitle>Como você quer usar seu saldo?</DialogTitle>
              <DialogDescription>
                Você tem R$ {balance.toFixed(2)} disponíveis para resgate.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant={selectedOption === 'subscription' ? 'default' : 'outline'}
                  className={cn(
                    'h-auto flex-col gap-2 p-4',
                    !isSubscriber && 'opacity-50 cursor-not-allowed'
                  )}
                  onClick={() => isSubscriber && setSelectedOption('subscription')}
                  disabled={!isSubscriber}
                >
                  <CreditCard className="h-6 w-6" />
                  <span className="text-center font-medium">
                    Desconto na
                    <br />
                    Assinatura
                  </span>
                </Button>

                <Button
                  variant={selectedOption === 'coupon' ? 'default' : 'outline'}
                  className="h-auto flex-col gap-2 p-4"
                  onClick={() => setSelectedOption('coupon')}
                >
                  <Ticket className="h-6 w-6" />
                  <span className="text-center font-medium">
                    Cupom para
                    <br />
                    Loja
                  </span>
                </Button>
              </div>

              {selectedOption === 'subscription' ? (
                <div className="bg-muted p-4 rounded-lg text-sm">
                  <p className="font-semibold mb-1">Resumo:</p>
                  <ul className="space-y-1 text-muted-foreground">
                    <li>Valor da Assinatura: R$ {subscriptionValue.toFixed(2)}</li>
                    <li>Saldo Utilizado: - R$ {Math.min(balance, subscriptionValue).toFixed(2)}</li>
                    <li className="pt-2 border-t font-semibold text-foreground">
                      Você pagará: R$ {Math.max(0, subscriptionValue - balance).toFixed(2)}
                    </li>
                  </ul>
                </div>
              ) : (
                <div className="grid gap-2">
                  <Label htmlFor="amount">Valor do Cupom</Label>
                  <Input
                    id="amount"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    max={balance}
                  />
                  <p className="text-xs text-muted-foreground">
                    Gera um código único para usar no checkout da loja.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <ChonkyButton onClick={handleConfirm} className="w-full sm:w-auto">
                Confirmar Resgate
              </ChonkyButton>
            </DialogFooter>
          </>
        )}

        {step === 'processing' && (
          <div className="py-12 flex flex-col items-center justify-center space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-lg font-medium">Processando seu pedido...</p>
            <p className="text-sm text-muted-foreground">Estamos gerando seu benefício.</p>
          </div>
        )}

        {step === 'success' && (
          <div className="py-6 flex flex-col items-center justify-center space-y-4 text-center">
            <div className="h-16 w-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-2">
              <CheckCircle2 className="h-10 w-10 text-green-600 dark:text-green-400" />
            </div>

            <DialogTitle className="text-2xl">Sucesso!</DialogTitle>

            {selectedOption === 'subscription' ? (
              <DialogDescription className="text-base">
                O desconto de <strong>R$ {Math.min(balance, subscriptionValue).toFixed(2)}</strong>{' '}
                foi aplicado na sua próxima renovação.
              </DialogDescription>
            ) : (
              <div className="w-full max-w-sm space-y-4">
                <DialogDescription>
                  Seu cupom de <strong>R$ {amount.toFixed(2)}</strong> foi gerado:
                </DialogDescription>

                <div className="flex items-center space-x-2">
                  <div className="grid flex-1 gap-2">
                    <Label htmlFor="link" className="sr-only">
                      Link
                    </Label>
                    <Input
                      id="link"
                      defaultValue="RAFO-X92-2024"
                      readOnly
                      className="text-center font-mono font-bold tracking-widest"
                    />
                  </div>
                  <Button type="submit" size="icon" className="px-3">
                    <span className="sr-only">Copy</span>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <Button onClick={() => onOpenChange(false)} className="mt-4 w-full">
              Fechar e ver saldo
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
